import React, { ReactElement } from 'react';
import PSPDFKitWeb from 'pspdfkit';
import Pagination from 'components/pspdf/components/pagination';
import { Selection } from 'components/pspdf/components/selection';
import Switch from 'components/switch';
import { Container, Controls, ZoomIn, ZoomOut } from './style';
import { toggleAllLinksVisibility } from 'components/pspdf/components/helpers';
import { attachMarkupMenuListeners } from 'components/pspdf/components/markup-menu/listeners';
import { customAnnotation } from 'components/pspdf/components/simple-custom-annotations';
import { useTranslation } from 'i18n';
import { noop } from 'lodash';
import { color } from 'styles/settings';
import {
  hideNameEditor,
  showNameEditor,
} from 'components/pspdf/components/snippet-name-editor/actions';
import useRenderedAt from 'hooks/use-rendered-at';
const { fromSerializableObject } = PSPDFKitWeb.Annotations;

/**
 * If we create snippets we want to link
 */

/***
 *
 * Flow
 *
 * 1. User uploads the document
 * 1.a) He does not want to link text snippets...everything is fine. He'll save them
 *
 * 1.b) He adds link annotations
 * 1.b) 1. We auto save the document and all the current changes
 * 1.b) 2. We create text snippet entry in our DB with data needed to represent the annotation
 *
 * If we create text snippet entries in our DB and generate instantJson slate data dynamically on the backend
 *
 * 1. We have to fetch document/text from the server every time user opens the form
 * 1.1 we fetch all the text snippets for document and append to instant JSON on the server.
 * How do we know if text snippet has another annotation on it? We'll have PSPDF kit annotation id on our text snippet entity.
 * We'll know to merge them if there is another annotation id inside instant JSON. We also have createdAt and updatedAt so we know the order.
 *
 * 2. We have to delete link text snippets if user has removed them from global link zone
 *
 *
 *
 * If we keep everything in slate or instantJSON
 *
 * Our client app needs to know about other format than nodes that can be linked.
 * How are we going to keep in sync client state before user actually saves the document or finishes the global links?
 * What happens when user deletes the link?
 *
 */

/***
 * ISSUES
 *
 * 1. User opens the document makes some changes(highlights, underlines, names text...), then selects text to link. Document automatically closes and link is added to global link zone. Do we auto save the changes? Or do we tell the user that he has unsaved changes?
 *
 * 2. User selects piece of text to link...document closes and goes back to the document. He does not have option to cancel
 * that link he started unless he closes the document goes to the global link zone and clears the link then goes back to the document.
 *
 * When to create text snippet for linking and how to clear them
 *
 * 1. If we create text snippets when user creates them...we could clean them up in beforeunload event. Or even tell the
 * user that he still has unlinked things in global link zone.
 *
 * 2. We can persist them accross page reload and the user has to clear them explicitly
 */

/***
 * When user creates a document, he must SAVE document before doing global linking from that document.
 *
 * User selects piece of text within PDF/Text editor:
 *
 * 1. He names it
 * 2. He does not name it
 *
 * User wants to link selected text:
 *
 *
 *
 * User wants to link selected text in document that is created but not saved with recent changes:
 */

interface Props {
  name?: string;
  url: string;
  onChange?: Function;
  instantJSON?: {
    annotations: any[];
  };
  value?: any;
  withForm?: boolean;
  editable?: boolean;
  customAnnotationRenderer: (
    annotation: any,
    isSelected: boolean,
    parentNode: HTMLElement,
    selection: Selection,
  ) => ReactElement;
  markupMenuRenderer: (
    selectedAnnotation: any,
    instance: any,
    actions: any,
    showFinishLinkAction?: boolean,
  ) => ReactElement;
  onAnnotationsChange?: (annotation: any, operation: string) => void;
  onTextSnippetSave?: (data: AnnotationCustomData) => void;
  onTextSnippetDelete?: (data: AnnotationCustomData) => void;
  onDeleteLink?: (data: AnnotationCustomData) => void;
  onStartLinking?: (data: AnnotationCustomData) => void;
  onFinishLinking?: (data: AnnotationCustomData) => void;
  onGlobalLinkIndicatorClick?: (data: AnnotationCustomData) => void;
  showFinishLinkAction?: boolean;
  onLoad(instance: any, instantJSON: any): void;
  onLinksToggle?(showAllLinks: boolean, instance: any): void;
}

interface AnnotationCustomData {
  annotationId: string;
  isLink?: boolean;
  isNamed?: boolean;
  name?: string;
  decorations?: string[];
}

const licenseKey = process.env.REACT_APP_PSPDFKIT_LICENCE_KEY;

const baseUrl = `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`;

export const configuration: any = {
  styleSheets: [
    '/pspdfkit-custom/pspdfkit.css',
    '/pspdfkit-custom/custom-components.css',
    '/pspdfkit-custom/snippet-name-editor.css',
    '/pspdfkit-custom/global-linking-indicator.css',
  ],
  toolbarItems: [
    { type: 'pager' },
    { type: 'annotate' },
    { type: 'highlighter' },
    { type: 'text-highlighter' },
    { type: 'zoom-in' },
    { type: 'zoom-out' },
    { type: 'zoom-mode' },
  ],
  licenseKey,
  baseUrl,
};

export default function PSPDFKit({
  onChange = noop,
  editable = true,
  instantJSON,
  url,
  ...props
}: Props) {
  const { t } = useTranslation();

  const renderedAt = useRenderedAt();

  const showAllLinksRef = React.useRef<boolean>(false);
  const instanceRef = React.useRef<any>();
  const selectedAnnotation = React.useRef<any>(null);
  const selectedText = React.useRef('');
  const container = React.useRef<HTMLDivElement>(null);
  const markupMenuRenderer = React.useRef<Function>(noop);
  const renderedAtRef = React.useRef<string | undefined>();
  const instance = instanceRef.current;
  const [isLoaded, setIsLoaded] = React.useState(false);

  function toggleAllLinks() {
    showAllLinksRef.current = !showAllLinksRef.current;
    toggleAllLinksVisibility(showAllLinksRef.current, instanceRef.current);

    props.onLinksToggle &&
      props.onLinksToggle(showAllLinksRef.current, instanceRef.current);
  }

  React.useEffect(() => {
    renderedAtRef.current = renderedAt;
  }, [renderedAt]);

  React.useEffect(() => {
    load().then(async (instance) => {
      instanceRef.current = instance;

      setIsLoaded(true);

      if (instantJSON?.annotations) {
        instantJSON?.annotations?.map((annotation) => {
          instance.create(fromSerializableObject(annotation));
        });
      }

      props.onLoad && props.onLoad(instance, instantJSON);

      instance.addEventListener('textSelection.change', (textSelection) => {
        if (textSelection) {
          // @ts-ignore
          textSelection.getText().then((text) => (selectedText.current = text));
        }
      });

      instance.addEventListener(
        'annotations.create',
        onAnnotationsCreate(instance),
      );

      instance.addEventListener(
        'annotations.update',
        onAnnotationsUpdate(instance),
      );

      instance.addEventListener(
        'annotations.delete',
        onAnnotationsDelete(instance),
      );

      attachMarkupMenuListeners(
        instance,
        selectedAnnotation,
        markupMenuRenderer,
        renderedAtRef,
        {
          onDeleteLink: props.onDeleteLink,
          onStartLinking: props.onStartLinking,
          onFinishLinking: props.onFinishLinking,
          onGlobalLinkIndicatorClick: props.onGlobalLinkIndicatorClick,
          onTextSnippetSave: props.onTextSnippetSave,
          onTextSnippetDelete: props.onTextSnippetDelete,
        },
      );
    });

    return unload;
  }, []);

  React.useEffect(() => {
    if (instance) {
      const {
        onTextSnippetSave,
        onTextSnippetDelete,
        onStartLinking,
        onFinishLinking,
        onDeleteLink,
      } = props;

      markupMenuRenderer.current = () => {
        const actions = {
          showNameEditor: () =>
            showNameEditor({
              selectedAnnotation,
              instance,
              // @ts-ignore
              callbacks: { onTextSnippetDelete, onTextSnippetSave },
            }),
          hideNameEditor: () => hideNameEditor({ instance }),
          onStartLinking,
          onFinishLinking,
          onDeleteLink,
        };

        return props.markupMenuRenderer(
          selectedAnnotation.current,
          instance,
          actions,
          props.showFinishLinkAction,
        );
      };
    }
  }, [instance, props.showFinishLinkAction, selectedAnnotation.current]);

  function onAnnotationsCreate(instance) {
    return async function (annotationInstance) {
      const [annotation] = annotationInstance.toJS();

      annotation.customData.text = selectedText.current;

      const instantJSON = await instance.exportInstantJSON();

      props.onAnnotationsChange &&
        props.onAnnotationsChange(annotation, 'create');

      onChange(instantJSON, {
        annotation,
        operation: 'create',
        text: selectedText.current,
      });
    };
  }

  function onAnnotationsUpdate(instance) {
    return async function (annotationInstance) {
      const [annotation] = annotationInstance.toJS();

      annotation.customData.text = selectedText.current;

      const instantJSON = await instance.exportInstantJSON();

      props.onAnnotationsChange &&
        props.onAnnotationsChange(annotation, 'update');

      onChange(instantJSON, {
        annotation,
        operation: 'update',
        text: selectedText.current,
      });
    };
  }

  function onAnnotationsDelete(instance) {
    return async function (annotationInstance) {
      const [annotation] = annotationInstance.toJS();

      const instantJSON = await instance.exportInstantJSON();

      props.onAnnotationsChange &&
        props.onAnnotationsChange(annotation, 'delete');

      onChange(instantJSON, {
        annotation,
        operation: 'delete',
        text: selectedText.current,
      });
    };
  }

  async function load() {
    if (container.current) {
      return PSPDFKitWeb.load({
        customRenderers: {
          Annotation: ({ annotation }) => {
            return customAnnotation(
              instanceRef.current,
              annotation,
              selectedAnnotation.current,
              props.customAnnotationRenderer,
              renderedAtRef,
            );
          },
        },
        document: url,
        container: container.current,
        ...configuration,
        ...(editable === false
          ? {
              toolbarItems: [
                { type: 'pager' },
                { type: 'zoom-in' },
                { type: 'zoom-out' },
                { type: 'zoom-mode' },
              ],
            }
          : {}),
      });
    } else {
      throw new Error(
        'The Container for the PSPDFKit instance needs to be defined before calling load().',
      );
    }
  }

  function unload() {
    if (instance) {
      instance.removeEventListener('annotations.create');
      instance.removeEventListener('annotations.update');
      instance.removeEventListener('annotations.delete');
      instance.removeEventListener('textSelection.change');
    }

    PSPDFKitWeb.unload(instance || container.current);
    instanceRef.current = undefined;
  }

  function zoomIn() {
    if (instance) {
      instance.setViewState((viewState) => viewState.zoomIn());
    }
  }

  function zoomOut() {
    if (instance) {
      instance.setViewState((viewState) => viewState.zoomOut());
    }
  }

  return (
    <Container>
      <div
        id="cortlex-pspdf"
        ref={container}
        style={{
          width: '100%',
          height: '660px',
          backgroundColor: color.ui.backgroundGray,
        }}
      />
      <Controls>
        {isLoaded && (
          <>
            <Pagination instance={instance} />
            <div>
              <Switch
                label={t('Show all links')}
                onChange={toggleAllLinks}
                checked={showAllLinksRef.current}
              />
              <ZoomIn onClick={zoomIn} />
              <ZoomOut onClick={zoomOut} />
            </div>
          </>
        )}
      </Controls>
    </Container>
  );
}
